import type { SearchFieldProps } from '@iheartjane/reefer';
import { SearchField } from '@iheartjane/reefer';

import { FormFieldWrapper } from '../formFieldWrapper/formFieldWrapper';

export type FormSearchFieldProps = Omit<SearchFieldProps, 'children'>;

/**
 * Search fields allow users to enter text into a UI to perform a search.
 *
 * Use this component *inside forms* for inputs of `type="search"`.
 *
 * _Field validations supported:_ none.
 *
 * **NOTE: DO NOT USE THIS COMPONENT OUTSIDE OF FORMS**
 *
 * _For a similar component for use outside of forms, see [`SearchField`](/story/components-forms-fields-searchfield--default)._
 */

export function FormSearchField({
  autoFocus = false,
  debounceDelay = 300,
  defaultValue = '',
  disabled = false,
  autocomplete,
  helperText,
  isDebounced = true,
  label,
  labelHidden = true,
  maxLength,
  minLength,
  name,
  onBlur,
  onChange,
  placeholder,
  'data-testid': testId,
  pattern,
  required = false,
  width = 'auto',
  skipRedundantDebouncedUpdate = false,
  ...props
}: FormSearchFieldProps) {
  return (
    <FormFieldWrapper
      defaultValue={defaultValue}
      maxLength={maxLength}
      minLength={minLength}
      name={name}
      pattern={pattern}
      render={({ children, field, invalid }) => (
        <SearchField
          aria-invalid={invalid}
          autoFocus={autoFocus}
          debounceDelay={debounceDelay}
          defaultValue={defaultValue}
          disabled={disabled}
          autocomplete={autocomplete}
          helperText={helperText}
          isDebounced={isDebounced}
          label={label}
          labelHidden={labelHidden}
          maxLength={maxLength}
          minLength={minLength}
          name={field.name}
          onBlur={(value) => {
            onBlur && onBlur(value);
            field.onBlur();
          }}
          onChange={(value) => {
            onChange && onChange(value);
            field.onChange(value);
          }}
          skipRedundantDebouncedUpdate={skipRedundantDebouncedUpdate}
          onUpdate={onChange}
          placeholder={placeholder}
          pattern={pattern}
          required={required}
          ref={field.ref}
          data-testid={testId}
          value={field.value}
          width={width}
          {...props}
        >
          {children}
        </SearchField>
      )}
      required={required}
    />
  );
}
